import { useState, useEffect } from 'react';
import { colors } from '@ui/kit';
import { loadScript, loadStyle } from '@utils/common';
import { getMediaplayerToken } from './api.get-media-player-token.js';

const MEDIA_PLAYER_VERSION = "1.11";
function useMediaPlayer(targetRef, input) {
  const [didError, setDidError] = useState(false);
  const [loading, setLoading] = useState(true);
  const displayInternalInfo = input.displayInternalInfo && input.caption !== undefined && !!input.caption.title && !!input.caption.description;
  useEffect(() => {
    const js = loadScript(`${process.env["NEXT_PUBLIC__MEDIA_LIBRARY_API"]}/MediaPlayer/${MEDIA_PLAYER_VERSION}/js/core/mediaplayer.js`);
    const css = loadStyle(`${process.env["NEXT_PUBLIC__MEDIA_LIBRARY_API"]}/MediaPlayer/${MEDIA_PLAYER_VERSION}/css/mediaplayer.css`);

    // Once all external libraries are ready, then return a reference to the
    // editor!
    Promise.all([getMediaplayerToken(), js, css]).then(async ([token]) => {
      const target = targetRef.current;
      if (!target || !token) {
        setDidError(true);
        setLoading(false);
        return;
      }
      jQuery.fn.mediaplayer.root = `${process.env["NEXT_PUBLIC__MEDIA_LIBRARY_API"]}/MediaPlayer/${MEDIA_PLAYER_VERSION}/`;
      jQuery.fn.mediaplayer.settings.paths.swf = `${process.env["NEXT_PUBLIC__MEDIA_LIBRARY_API"]}/MediaPlayer/${MEDIA_PLAYER_VERSION}/js/libraries/video-js/video-js.swf`;
      jQuery.fn.mediaplayer.settings.paths.plugins = `${process.env["NEXT_PUBLIC__MEDIA_LIBRARY_API"]}/MediaPlayer/${MEDIA_PLAYER_VERSION}/js/plugins/`;
      // @ts-expect-error: typings are wrong!
      jQuery.fn.mediaplayer.settings.paths.token = `${process.env["NEXT_PUBLIC__MEDIA_LIBRARY_API"]}/api/token`;
      $(target).mediaplayer({
        autoplay: input.autoPlay,
        colors: {
          theme: {
            backgroundColor: {
              color: input.type === "audio" ? "#ffffff" : "#f3f3f3",
              opacity: input.type === "audio" ? 0 : 1
            },
            baseColor: colors.publisher(),
            hoverColor: colors.publisher(),
            iconColor: colors.text.primary(),
            textColor1: colors.text.inversed(),
            // Configures video player description text
            textColor2: colors.text.primary() // Configures video player bottom bar + audio player
          }
        },
        title: input.caption?.title?.value,
        description: input.caption?.description?.value,
        showOverlay: displayInternalInfo,
        id: input.src,
        onError: () => {
          setDidError(true);
          setLoading(false);
        },
        preload: false,
        token,
        type: input.type,
        // @ts-expect-error Plugins are not explictly typed, but it is
        // necessary to override them in order to hide the metadata overlay
        // (showOverlay is not enough)
        plugins: !displayInternalInfo ? {
          HD: {},
          Thumbnails: {}
        } : undefined
      });
      await waitForPlayer(targetRef);
      setLoading(false);
    }).catch(err => {
      console.warn(err);
      setDidError(true);
      setLoading(false);
    });
  }, [targetRef, input.autoPlay, input.type, displayInternalInfo, input.caption?.title, input.caption?.description, input.src]);
  return {
    didError,
    loading
  };
}
const WAIT_FOR_TIMEOUT = 8_000;
function waitForPlayer(targetRef) {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();

    // Internal helper that's called once every 50ms, and determines if
    // the video.js player is currently waiting to be initialized
    const checkForWaitingClass = () => {
      const target = targetRef.current;

      // If the .vjs-waiting class doesn't exist any more, then we can
      // assume that video.js is ready
      if (target && !target.classList.contains("loading") && $(target).find(".video-js:first").length === 1) {
        resolve();

        // Abort further looping
        return;
      }
      if (Date.now() < startedAt + WAIT_FOR_TIMEOUT) {
        // ... Otherwise try again in 50ms
        setTimeout(checkForWaitingClass, 50);
      } else {
        reject();
      }
    };

    // Start checking immediately
    checkForWaitingClass();
  });
}

export { useMediaPlayer };
//# sourceMappingURL=hook.media-player.js.map
